import type { SqmBand } from '../types/db';

/**
 * 5-year construction-year bands. Half-open intervals: '1990-1995' means the
 * range [1990, 1994] inclusive — a property built in 1995 lives in the
 * next band ('1995-2000'). This eliminates ambiguity at the boundaries.
 *
 * '<1960' captures older legal stock (older than that is rare and usually
 * αυθαιρεσίες-heavy — not actively crawled but kept as a storage band).
 *
 * '2025+' is the open-ended new-construction tail.
 *
 * '*' is the sentinel for "any year / unknown" — used by sources that have
 * no per-row year data (eauctionstats, BoE) and as the always-present
 * fallback row for the scoring lookup ladder.
 */

/** The bands the cross-product crawler actively iterates. */
export const ALL_YEAR_BANDS_FOR_CRAWL = [
  '1960-1965',
  '1965-1970',
  '1970-1975',
  '1975-1980',
  '1980-1985',
  '1985-1990',
  '1990-1995',
  '1995-2000',
  '2000-2005',
  '2005-2010',
  '2010-2015',
  '2015-2020',
  '2020-2025',
  '2025+',
] as const;

export type CrawlYearBand = (typeof ALL_YEAR_BANDS_FOR_CRAWL)[number];

export type YearBand = CrawlYearBand | '<1960' | '*';

export const YEAR_BAND_ANY = '*' as const;

/**
 * Map a construction year to its band.
 *   yearToBand(1987) → '1985-1990'
 *   yearToBand(1958) → '<1960'
 *   yearToBand(2027) → '2025+'
 *   yearToBand(null) → '*'
 */
export function yearToBand(year: number | null | undefined): YearBand {
  if (year == null || !Number.isFinite(year)) return YEAR_BAND_ANY;
  if (year < 1960) return '<1960';
  if (year >= 2025) return '2025+';
  const start = Math.floor(year / 5) * 5;
  return `${start}-${start + 5}` as CrawlYearBand;
}

/**
 * Inverse of `yearToBand` for URL building. Returns inclusive [from, to]
 * year bounds for the band, or null for the '*' sentinel.
 *
 * For '2025+': to is null (open-ended at the high end → no `eos` URL part).
 */
export function bandToYearRange(
  band: YearBand,
): { from: number; to: number | null } | null {
  if (band === YEAR_BAND_ANY) return null;
  if (band === '<1960') return { from: 1900, to: 1959 };
  if (band === '2025+') return { from: 2025, to: null };
  const [startStr, endStr] = band.split('-');
  if (!startStr || !endStr) return null;
  return { from: Number(startStr), to: Number(endStr) - 1 };
}

/** Inclusive [from, to] sqm bounds for each band; '140+' has to=null. */
export function sqmBandToRange(
  band: SqmBand,
): { from: number; to: number | null } {
  switch (band) {
    case '30-50':
      return { from: 30, to: 49 };
    case '50-80':
      return { from: 50, to: 79 };
    case '80-110':
      return { from: 80, to: 109 };
    case '110-140':
      return { from: 110, to: 139 };
    case '140+':
      return { from: 140, to: null };
  }
}

/**
 * Build a Spitogatos search URL for a (slug, sqm_band, year_band, page) combo.
 *
 * URL pattern (verified empirically):
 *   /pwliseis-katoikies/{slug}/emvado_apo-X[/emvado_eos-Y]
 *                              [/etos_kataskevis_apo-X[/etos_kataskevis_eos-Y]]
 *                              [/selida_N]
 *
 * The closing `eos` segments are omitted when the band is open-ended on the
 * high side ('140+' for sqm, '2025+' for year). Pages > 1 append /selida_N.
 */
export function buildSpitogatosUrl(args: {
  urlSlug: string;
  sqmBand: SqmBand;
  yearBand: YearBand;
  page?: number;
}): string {
  const segments: string[] = [];

  const sqm = sqmBandToRange(args.sqmBand);
  segments.push(`emvado_apo-${sqm.from}`);
  if (sqm.to != null) segments.push(`emvado_eos-${sqm.to}`);

  const year = bandToYearRange(args.yearBand);
  if (year) {
    segments.push(`etos_kataskevis_apo-${year.from}`);
    if (year.to != null) segments.push(`etos_kataskevis_eos-${year.to}`);
  }

  const page = args.page ?? 1;
  if (page > 1) segments.push(`selida_${page}`);

  return `https://www.spitogatos.gr/pwliseis-katoikies/${args.urlSlug}/${segments.join('/')}`;
}
